import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import apiService from '../services/apiService';
import { isQaEnvironment } from '../utils/subdomain';
import './SyncAmbientes.css';

/**
 * Sincronizador — passo 3: escolher item a item.
 *
 * O requisito é escolher ITENS, não entidades. O caso que justifica isso está em
 * leroymerlin: QA tem 5 programas e PROD tem 1, e um dos ausentes chama-se
 * "Programa Leroy Merlin - Teste V1". Um botão "sincronizar programas" levaria um programa
 * de teste para produção.
 *
 * Três estados por item, não dois:
 *   ausente     -> existe na origem e não no destino. Cria.
 *   divergente  -> existe nos dois com conteúdo diferente. Atualiza.
 *   igual       -> nada a fazer; escondido por padrão.
 *
 * A visão padrão é "ausente", que é o pedido. "Divergente" fica atrás de filtro porque
 * sobrescrever algo que já roda é decisão de outra natureza.
 *
 * O escopo aparece em destaque: um item de escopo GLOBAL pode reavaliar o tenant inteiro,
 * então "1 item" não significa impacto pequeno.
 */

const NOME = { prod: 'Produção', qa: 'QA', hmg: 'Homologação' };
const NOME_DESTINO = { prod: 'produção', qa: 'QA', hmg: 'homologação' };

const DIRECOES = {
    'prod-qa':  { origem: 'prod', destino: 'qa',   classe: 'atendimentos' },
    'prod-hmg': { origem: 'prod', destino: 'hmg',  classe: 'atendimentos' },
    // Mesmo par de ambientes, outra faixa: alinhar HMG com produção. Existe
    // porque QA acumula dado de teste, então `qa-hmg` não serve para isso.
    'prod-hmg-config': { origem: 'prod', destino: 'hmg', classe: 'config' },
    'qa-hmg':   { origem: 'qa',   destino: 'hmg',  classe: 'config' },
    'prod-qa-config': { origem: 'prod', destino: 'qa', classe: 'config' },
    'hmg-qa-config':  { origem: 'hmg',  destino: 'qa', classe: 'config' },
    'qa-prod':  { origem: 'qa',   destino: 'prod', classe: 'config' }
};

export default function SyncSelecao({ user }) {
    const { direcao: idDirecao, tenant, entidade } = useParams();
    const ehQa = isQaEnvironment();
    const dir = DIRECOES[idDirecao];

    const corteDaUrl = new URLSearchParams(window.location.search).get('corte');

    const [dados, setDados] = useState(null);
    const [filtro, setFiltro] = useState('ausente');
    const [busca, setBusca] = useState('');
    const [marcados, setMarcados] = useState(() => new Set());
    const [erro, setErro] = useState(null);
    const [carregando, setCarregando] = useState(true);
    const [salvando, setSalvando] = useState(false);

    const carregar = useCallback(async () => {
        setCarregando(true);
        setErro(null);
        try {
            const d = await apiService.syncItems({
                origem: dir.origem, destino: dir.destino,
                subdomain: tenant, entidade, corte: corteDaUrl || null
            });
            setDados(d);
        } catch (e) {
            setErro(e?.response?.data?.error || e.message);
        } finally {
            setCarregando(false);
        }
    }, [dir, tenant, entidade, corteDaUrl]);

    useEffect(() => {
        if (!ehQa || !user?.can_sync || !dir) { setCarregando(false); return; }
        carregar();
    }, [ehQa, user, dir, carregar]);

    // Universo de seleção = itens da ORIGEM + itens que só existem no DESTINO. Os dois são
    // selecionáveis, com ações opostas: origem leva (cria/atualiza), destino sobra (remove).
    //
    // A chave de seleção é COMPOSTA (`O:` / `D:`) porque chave de negócio pode coincidir
    // entre os dois lados: o casamento é por `correspondencia`, não por chave, então um item
    // da origem pode não casar e um do destino com a MESMA chave pode sobrar. Com um Set de
    // chaves cruas os dois viravam um.
    const selKey = (i) => (i.estado === 'so_destino' ? 'D:' : 'O:') + i.chave;

    const universo = useMemo(() => [
        ...(dados?.itens || []),
        ...(dados?.so_no_destino || []).map((i) => ({ ...i, estado: 'so_destino' }))
    ], [dados]);

    const visiveis = useMemo(() => {
        const itens = universo.filter((i) => i.estado === filtro);
        const q = busca.trim().toLowerCase();
        return q ? itens.filter((i) => (i.rotulo || '').toLowerCase().includes(q)
                                    || (i.chave || '').toLowerCase().includes(q)) : itens;
    }, [universo, filtro, busca]);

    const globaisMarcados = useMemo(() => universo
        .filter((i) => marcados.has(selKey(i)) && i.escopo === 'global').length,
        [universo, marcados]);

    const removeraoMarcados = useMemo(() => universo
        .filter((i) => marcados.has(selKey(i)) && i.estado === 'so_destino').length,
        [universo, marcados]);

    if (!ehQa || !user?.can_sync || !dir) {
        return (
            <div className="sync-wrap">
                <div className="sync-bloqueio"><h1>Sincronizador de Ambientes</h1>
                    <p>Acesso restrito.</p></div>
            </div>
        );
    }

    function alternar(chave) {
        setMarcados((atual) => {
            const novo = new Set(atual);
            if (novo.has(chave)) novo.delete(chave); else novo.add(chave);
            return novo;
        });
    }

    function marcarVisiveis(ligar) {
        setMarcados((atual) => {
            const novo = new Set(atual);
            visiveis.forEach((i) => { if (ligar) novo.add(selKey(i)); else novo.delete(selKey(i)); });
            return novo;
        });
    }

    async function salvarPlano() {
        if (!marcados.size || salvando) return;
        setSalvando(true);
        setErro(null);
        try {
            const escolhidos = universo.filter((i) => marcados.has(selKey(i)));
            const r = await apiService.syncCriarPlano({
                direcao: idDirecao,
                origem: dir.origem,
                destino: dir.destino,
                subdomain: tenant,
                classe: dir.classe,
                // O corte vem do diff e é o MESMO usado nos dois lados. Não recalcular
                // aqui: recalcular tornaria o número aprovado diferente do que vai.
                corte: dados.corte,
                itens: escolhidos.map((i) => ({
                    entidade,
                    chave: i.chave,
                    rotulo: i.rotulo,
                    escopo: i.escopo,
                    fingerprint: i.fingerprint,
                    estado: i.estado,
                    selecao: 'manual'
                }))
            });
            window.location.href = `/sync/plano/${r.plan_id}`;
        } catch (e) {
            setErro(e?.response?.data?.error || e.message);
            setSalvando(false);
        }
    }

    const resumo = dados?.resumo || {};

    return (
        <div className="sync-wrap">
            <header className="sync-head">
                <a className="sync-voltar"
                   href={`/sync/${idDirecao}/${encodeURIComponent(tenant)}`}>← Voltar ao diff</a>
                <h1>
                    {dados?.label || entidade}
                    <span className="sync-tenant">{tenant}</span>
                </h1>
                <p className="sync-sub">
                    {NOME[dir.origem]} <span className="sync-seta">→</span> {NOME[dir.destino]}
                    {dados?.corte && <> · corte {dados.corte.replace('T', ' ').slice(0, 19)}</>}
                </p>
            </header>

            {erro && <div className="sync-erro">⚠️ {erro}</div>}
            {carregando && <div className="sync-info">Comparando os itens…</div>}

            {!carregando && dados && (
                <>
                    <div className="sync-resumo">
                        {[['ausente', `${resumo.ausente || 0} ausentes no destino`],
                          ['divergente', `${resumo.divergente || 0} com conteúdo diferente`],
                          ['igual', `${resumo.igual || 0} idênticos`]].map(([k, txt]) => (
                            <button key={k} type="button"
                                className={`sync-chip filtro ${k === 'ausente' ? 'dif' : (k === 'igual' ? 'igual' : 'warn')}`
                                    + (filtro === k ? ' ativo' : '')}
                                onClick={() => setFiltro(k)}>{txt}</button>
                        ))}
                        {resumo.so_no_destino > 0 && (
                            <button key="so_destino" type="button"
                                className={'sync-chip filtro dif'
                                    + (filtro === 'so_destino' ? ' ativo' : '')}
                                title="Existem no destino e não na origem. Selecionar remove no destino (soft delete), e o desfazer do plano restaura."
                                onClick={() => setFiltro('so_destino')}>
                                {resumo.so_no_destino} só no destino
                            </button>
                        )}
                    </div>

                    {dados.truncado && (
                        <p className="sync-nota destaque">
                            Mostrando <strong>{dados.total_listado}</strong> de{' '}
                            <strong>{dados.total_disponivel}</strong> itens da origem
                            {dados.ordem === 'desc' && ' (dos mais recentes para os mais antigos)'}.
                            A lista foi cortada pelo limite de leitura — refine com a busca antes de
                            montar o plano, senão você aprova uma amostra pensando que é o todo.
                        </p>
                    )}

                    <div className="sync-barra">
                        <input className="sync-select" type="search" value={busca}
                               placeholder="Buscar por nome…"
                               onChange={(e) => setBusca(e.target.value)} />
                        <button type="button" className="sync-botao-linha"
                                onClick={() => marcarVisiveis(true)}>marcar os {visiveis.length} visíveis</button>
                        <button type="button" className="sync-botao-linha"
                                onClick={() => marcarVisiveis(false)}>desmarcar</button>
                    </div>

                    <div className="sync-tabela-scroll">
                        <table className="sync-tabela">
                            <thead>
                                <tr>
                                    <th style={{ width: '2.5rem' }}> </th>
                                    <th>Item</th>
                                    <th>No destino</th>
                                    <th>Escopo</th>
                                    <th>Chave</th>
                                </tr>
                            </thead>
                            <tbody>
                                {visiveis.map((i) => (
                                    <tr key={selKey(i)}
                                        className={marcados.has(selKey(i)) ? 'linha-marcada' : ''}>
                                        <td>
                                            <input type="checkbox"
                                                   checked={marcados.has(selKey(i))}
                                                   onChange={() => alternar(selKey(i))}
                                                   aria-label={`Selecionar ${i.rotulo || i.chave}`} />
                                        </td>
                                        <td>
                                            {i.estado === 'so_destino' && (
                                                <span className="sync-badge dif"
                                                      style={{ marginRight: 6 }}
                                                      title="Este item só existe no destino. Selecionar REMOVE lá (soft delete), e o desfazer do plano restaura.">
                                                    remover
                                                </span>
                                            )}
                                            {i.rotulo || <em>(sem rótulo)</em>}
                                            {i.ambiguo && (
                                                <span className="sync-badge warn"
                                                      style={{ marginLeft: 6 }}
                                                      title="Há mais de um item com esta mesma identidade em um dos lados, então não foi possível dizer com qual ele corresponde. A simulação resolve isso e recusa se continuar ambíguo.">
                                                    ambíguo
                                                </span>
                                            )}
                                        </td>
                                        <td className="sync-nota">
                                            {i.estado === 'so_destino'
                                                ? <em>não existe na origem</em>
                                                : (i.rotulo_destino || <em>não encontrado</em>)}
                                        </td>
                                        <td>
                                            <span className={`sync-badge escopo-${i.escopo}`}>
                                                {i.escopo === 'global' ? 'GLOBAL' : i.escopo}
                                            </span>
                                        </td>
                                        <td className="num">{i.chave}</td>
                                    </tr>
                                ))}
                                {visiveis.length === 0 && (
                                    <tr><td colSpan={5} style={{ color: '#6c7d80' }}>
                                        Nenhum item {filtro} {busca && 'com esse texto'}.
                                    </td></tr>
                                )}
                            </tbody>
                        </table>
                    </div>

                    {removeraoMarcados > 0 && (
                        <p className="sync-aviso-alto">
                            <strong>{removeraoMarcados}</strong>{' '}
                            {removeraoMarcados === 1 ? 'item marcado será REMOVIDO no destino'
                                                     : 'itens marcados serão REMOVIDOS no destino'}
                            {' '}({NOME_DESTINO[dir.destino] || dir.destino}).
                            É soft delete e o desfazer do plano restaura — mas remoção que
                            deixaria referência pendurada é recusada na simulação, com o nome
                            de quem depende. Rode a simulação antes de aplicar.
                        </p>
                    )}

                    {globaisMarcados > 0 && (
                        <p className="sync-aviso-alto">
                            <strong>{globaisMarcados}</strong>{' '}
                            {globaisMarcados === 1 ? 'item marcado é de escopo global'
                                                   : 'itens marcados são de escopo global'}.
                            Escopo global vale para o cliente inteiro — em prompt, isso reavalia
                            todos os atendimentos do tenant. Confira antes de aprovar.
                        </p>
                    )}

                    <div className="sync-barra">
                        <button type="button" className="sync-botao-principal"
                                disabled={!marcados.size || salvando}
                                onClick={salvarPlano}>
                            {salvando ? 'Gravando…' : `Montar plano com ${marcados.size} ${marcados.size === 1 ? 'item' : 'itens'} →`}
                        </button>
                        <span className="sync-nota">
                            O plano é gravado para revisão. Nada é aplicado neste passo.
                        </span>
                    </div>
                </>
            )}
        </div>
    );
}
